import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dates } from 'investira.sdk';
import services from '../services';
import {
  buildComparadorFundosPath,
  COMPARADOR_FUNDOS_MAX_ITEMS,
  parseComparadorFundosSearch
} from '../pagesPublic/ComparadorFundos/_shared/comparadorFundos.helpers';
import {
  comparadorFundosDataReducer,
  createComparadorFundosDataState,
  normalizeComparadorFundosCotacao,
  normalizeComparadorFundosDetalhe
} from '../pagesPublic/ComparadorFundos/_shared/comparadorFundos.data';
import utils from '../utils';

const ComparadorFundosContext = React.createContext();
const wInicio = dates.addYears(dates.toSqlDate(dates.addDays(new Date(), -1)), -5);
const wDataInicio = utils.dates.lastMonthDayValid(15, wInicio);
const wLoadableSources = ['detalhe', 'cotacao'];

const initialSelectionState = {
  selectedFundosMap: {},
  limitNotice: '',
  selectionNotice: ''
};

function pvSelectionReducer(pState, pAction) {
  if (pAction.type === 'FUNDO_REMEMBERED') {
    return {
      ...pState,
      selectedFundosMap: {
        ...pState.selectedFundosMap,
        [pAction.fundoId]: pAction.fundo
      }
    };
  }

  if (pAction.type === 'LIMIT_NOTICE_CHANGED') {
    return {
      ...pState,
      limitNotice: pAction.notice
    };
  }

  if (pAction.type === 'SELECTION_NOTICE_CHANGED') {
    return {
      ...pState,
      selectionNotice: pAction.notice
    };
  }

  return pState;
}

function pvReadRawFundosIds(pSearch = '') {
  const xParams = new URLSearchParams(pSearch);

  return String(xParams.get('fundos') || '')
    .split(',')
    .map(pValue => pValue.trim())
    .filter(Boolean);
}

function pvGetFundoId(pFundo = {}) {
  return String(pFundo?.ifeed_ativo_id || pFundo?.ativo_id || '').trim();
}

function pvGetErrorMessage(pError) {
  return pError?.response?.data?.message || pError?.message || 'Fonte indisponível.';
}

export const useComparadorFundosContext = () => React.useContext(ComparadorFundosContext);

export const ComparadorFundosProvider = memo(({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dataState, dataDispatch] = React.useReducer(
    comparadorFundosDataReducer,
    undefined,
    createComparadorFundosDataState
  );
  const [selectionState, selectionDispatch] = React.useReducer(
    pvSelectionReducer,
    initialSelectionState
  );
  const requestIdRef = useRef(0);
  const loadedFundosRef = useRef(new Set());
  const rejectedFundosRef = useRef(new Set());
  const selectedFundosIds = useMemo(
    () => parseComparadorFundosSearch(location.search),
    [location.search]
  );
  const ignoredSelectionNotice = useMemo(() => {
    const xRawFundosIds = pvReadRawFundosIds(location.search);

    return xRawFundosIds.length !== selectedFundosIds.length
      ? 'Ids inválidos, repetidos ou excedentes da URL foram ignorados no estado central.'
      : '';
  }, [location.search, selectedFundosIds]);
  const selectedFundosIdsRef = useRef(selectedFundosIds);

  selectedFundosIdsRef.current = selectedFundosIds;

  useEffect(() => {
    const xCanonicalPath = buildComparadorFundosPath(selectedFundosIds);
    const xCurrentPath = location.pathname + location.search;

    if (xCanonicalPath !== xCurrentPath) {
      selectionDispatch({
        type: 'SELECTION_NOTICE_CHANGED',
        notice: 'Ids vazios, inválidos, repetidos ou excedentes foram retirados da URL.'
      });
      navigate(xCanonicalPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate, selectedFundosIds]);

  const loadSource = useCallback(
    (pFundoId, pSource) => {
      if (!wLoadableSources.includes(pSource)) {
        return;
      }

      requestIdRef.current += 1;
      const xRequestId = requestIdRef.current;

      dataDispatch({
        type: 'SOURCE_LOADING',
        fundoId: pFundoId,
        source: pSource,
        requestId: xRequestId
      });

      /** Retira da seleção um ID cujo detalhe não comprovou um fundo FND. */
      const xRejectFundo = () => {
        rejectedFundosRef.current.add(pFundoId);
        const xAcceptedIds = selectedFundosIdsRef.current.filter(
          pId => !rejectedFundosRef.current.has(pId)
        );

        selectionDispatch({
          type: 'SELECTION_NOTICE_CHANGED',
          notice: `O fundo ${pFundoId} não pôde ser resolvido como FND e foi retirado da seleção.`
        });
        navigate(buildComparadorFundosPath(xAcceptedIds), { replace: true });
      };
      const xHandleSuccess = pNormalizer => rResponse => {
        const xData = pNormalizer(rResponse);

        if (pSource === 'detalhe' && (!xData || xData.mercado !== 'FND')) {
          xRejectFundo();
          return;
        }

        dataDispatch({
          type: 'SOURCE_READY',
          fundoId: pFundoId,
          source: pSource,
          requestId: xRequestId,
          data: xData
        });
      };
      const xHandleError = rError => {
        if (pSource === 'detalhe') {
          xRejectFundo();
          return;
        }

        dataDispatch({
          type: 'SOURCE_ERROR',
          fundoId: pFundoId,
          source: pSource,
          requestId: xRequestId,
          error: pvGetErrorMessage(rError)
        });
      };

      if (pSource === 'detalhe') {
        services.ativosbase.read(
          {
            ifeed_ativo_id: pFundoId,
            requestKey: pFundoId
            // params: { vencimento: 'null' }
          },
          xHandleSuccess(normalizeComparadorFundosDetalhe),
          xHandleError
        );
      }

      if (pSource === 'cotacao') {
        services.ativosbase.cotacao(
          {
            ifeed_ativo_id: pFundoId,
            requestKey: pFundoId,
            data_inicio: wDataInicio
          },
          xHandleSuccess(normalizeComparadorFundosCotacao),
          xHandleError
        );
      }
    },
    [navigate]
  );

  const loadFundo = useCallback(
    pFundoId => {
      loadSource(pFundoId, 'detalhe');
      loadSource(pFundoId, 'cotacao');
    },
    [loadSource]
  );

  useEffect(() => {
    const xSelectedIds = new Set(selectedFundosIds);

    selectedFundosIds.forEach(pFundoId => {
      if (!loadedFundosRef.current.has(pFundoId)) {
        loadedFundosRef.current.add(pFundoId);
        loadFundo(pFundoId);
      }
    });

    loadedFundosRef.current.forEach(pFundoId => {
      if (!xSelectedIds.has(pFundoId)) {
        services.ativosbase.cancelRead(pFundoId);
        services.ativosbase.cancelCotacao(pFundoId);
        loadedFundosRef.current.delete(pFundoId);
        rejectedFundosRef.current.delete(pFundoId);
      }
    });
  }, [loadFundo, selectedFundosIds]);

  useEffect(() => {
    return () => {
      loadedFundosRef.current.forEach(pFundoId => {
        services.ativosbase.cancelRead(pFundoId);
        services.ativosbase.cancelCotacao(pFundoId);
      });
      loadedFundosRef.current.clear();
    };
  }, []);

  function handleToggleFundo(pFundo) {
    const xFundoId = pvGetFundoId(pFundo);

    if (!xFundoId) {
      return;
    }

    if (selectedFundosIds.includes(xFundoId)) {
      selectionDispatch({ type: 'LIMIT_NOTICE_CHANGED', notice: '' });
      navigate(buildComparadorFundosPath(selectedFundosIds.filter(pItem => pItem !== xFundoId)), {
        replace: true
      });
      return;
    }

    if (selectedFundosIds.length >= COMPARADOR_FUNDOS_MAX_ITEMS) {
      selectionDispatch({
        type: 'LIMIT_NOTICE_CHANGED',
        notice: 'Você já selecionou 3 fundos. Remova um fundo antes de adicionar outro.'
      });
      return;
    }

    selectionDispatch({ type: 'FUNDO_REMEMBERED', fundoId: xFundoId, fundo: pFundo });
    selectionDispatch({ type: 'LIMIT_NOTICE_CHANGED', notice: '' });
    navigate(buildComparadorFundosPath([...selectedFundosIds, xFundoId]), { replace: true });
  }

  /** Adiciona um fundo pelo diálogo sem transformar seleção repetida em remoção. */
  function handleAddFundo(pFundo) {
    const xFundoId = pvGetFundoId(pFundo);

    if (!xFundoId || selectedFundosIds.includes(xFundoId)) {
      return;
    }

    if (selectedFundosIds.length >= COMPARADOR_FUNDOS_MAX_ITEMS) {
      selectionDispatch({
        type: 'LIMIT_NOTICE_CHANGED',
        notice: 'Você já selecionou 3 fundos. Remova um fundo antes de adicionar outro.'
      });
      return;
    }

    selectionDispatch({ type: 'FUNDO_REMEMBERED', fundoId: xFundoId, fundo: pFundo });
    selectionDispatch({ type: 'LIMIT_NOTICE_CHANGED', notice: '' });
    navigate(buildComparadorFundosPath([...selectedFundosIds, xFundoId]), { replace: true });
  }

  /** Remove um fundo e compacta a lista ordenada na URL. */
  function handleRemoveFundo(pEvent, pFundoId) {
    pEvent && pEvent.stopPropagation(); // Impede o evento de chegar ao elemento pai
    pEvent && pEvent.preventDefault(); // Evita o comportamento padrão, se necessário

    const xFundoId = String(pFundoId);

    selectionDispatch({ type: 'LIMIT_NOTICE_CHANGED', notice: '' });
    navigate(buildComparadorFundosPath(selectedFundosIds.filter(pItem => pItem !== xFundoId)), {
      replace: true
    });
  }

  /** Troca somente o slot indicado sem alterar a ordem dos demais. */
  function handleReplaceFundo(pIndex, pFundo) {
    const xFundoId = pvGetFundoId(pFundo);

    if (!xFundoId || selectedFundosIds.includes(xFundoId) || !selectedFundosIds[pIndex]) {
      return;
    }

    const xNextIds = [...selectedFundosIds];

    xNextIds[pIndex] = xFundoId;
    selectionDispatch({ type: 'FUNDO_REMEMBERED', fundoId: xFundoId, fundo: pFundo });
    navigate(buildComparadorFundosPath(xNextIds), { replace: true });
  }

  function retrySource(pFundoId, pSource) {
    loadSource(String(pFundoId), pSource);
  }

  const state = {
    ...dataState,
    ...selectionState,
    selectedFundosIds,
    ignoredSelectionNotice
  };

  const actions = {
    handleToggleFundo,
    handleAddFundo,
    handleRemoveFundo,
    handleReplaceFundo,
    retrySource,
    loadFundo
  };

  return (
    <ComparadorFundosContext.Provider value={{ state, actions }}>
      {children}
    </ComparadorFundosContext.Provider>
  );
});

export default ComparadorFundosProvider;
